import { ChangeEvent, useState } from 'react';

import cx from 'classnames';

import Back from 'components/Icons/ArrowLeftCircle.svg';
import Close from 'components/Icons/Close.svg';

import {
  PostBookingSurveyAnyCategory as Category,
  PostBookingSurveySchema,
} from 'types/post-booking-survey';

import { PostBookingSurveyCategory } from './PostBookingSurveyCategory';
import { usePostBookingSurveyEvents } from './usePostBookingSurveyEvents';

import s from './PostBookingSurveyForm.module.css';

type Props = {
  schema: PostBookingSurveySchema;
  onClose: () => void;
  onSubmit: (category: string, answer: string, finish: boolean) => void;
  ariaLabels?: string;
};

export const PostBookingSurveyForm = ({
  schema,
  onSubmit,
  onClose,
  ariaLabels,
}: Props) => {
  const { trackGoBack } = usePostBookingSurveyEvents();
  const [step, setStep] = useState<1 | 2>(1);
  const [otherAnswer, setOtherAnswer] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category>();

  function onChangeOther(event: ChangeEvent<HTMLInputElement>) {
    setOtherAnswer(event.target.value);
    if (step === 1) setSelectedCategory(undefined);
    if (step === 2) setSelectedAnswer('');
  }

  function onSelectCategory(category: Category) {
    setSelectedCategory(category);
    setOtherAnswer('');
    onSubmit(category.name, '', false);
  }

  function onGoBack() {
    trackGoBack(selectedCategory?.name ?? '');
    setStep(1);
    setOtherAnswer('');
    setSelectedAnswer('');
  }

  function onSubmitOther() {
    onSubmit(selectedCategory?.name ?? '', otherAnswer, true);
  }

  function onFocusOtherInput() {
    if (step === 1) {
      setSelectedCategory(undefined);
    }

    if (step === 2) {
      setSelectedAnswer('');
    }
  }

  function nextStep() {
    setStep(2);
  }

  function submitSelectedAnswer() {
    onSubmit(selectedCategory?.name ?? '', selectedAnswer, true);
  }

  function onAnswer(answer: string) {
    setSelectedAnswer(answer);
    if (selectedCategory?.type !== 'text') {
      onSubmit(selectedCategory?.name ?? '', answer, false);
    }
  }

  function onSkip() {
    setSelectedAnswer('');
    onSubmit(selectedCategory?.name ?? '', '', true);
  }

  const allowOther =
    selectedCategory && step === 2
      ? selectedCategory.type === 'select' && selectedCategory.allowOther
      : schema.allowOther;

  return (
    <>
      <div className={s.modalHeader}>
        <button
          type="button"
          onClick={onGoBack}
          disabled={step === 1}
          className={cx(s.iconButton, s.backButton)}
          aria-label="Go back"
        >
          <Back viewBox="0 0 30 30" />
        </button>
        <span className={s.stepText}>{step} of 2</span>
        <button
          type="button"
          onClick={onClose}
          className={cx(s.iconButton, 'closeButton')}
          aria-label="Close"
        >
          <Close />
        </button>
      </div>
      <h2 className={s.heading} id={ariaLabels}>
        {selectedCategory && step === 2
          ? selectedCategory.prompt
          : schema.prompt}
      </h2>

      <div className={s.options}>
        {selectedCategory && step === 2 ? (
          <PostBookingSurveyCategory
            answer={selectedAnswer}
            category={selectedCategory}
            onChange={onAnswer}
          />
        ) : (
          schema.categories.map((c, i) => (
            <button
              key={i}
              type="button"
              className={cx(s.selectButton, selectedCategory === c && s.active)}
              onClick={() => onSelectCategory(c)}
            >
              {c.name}
            </button>
          ))
        )}

        {allowOther && (
          <>
            <input
              type="text"
              maxLength={50}
              placeholder="Add Other Option"
              onFocus={onFocusOtherInput}
              value={otherAnswer}
              onChange={onChangeOther}
            />

            {otherAnswer && (
              <button
                type="button"
                className={cx('brand-primary-color', s.submitButton)}
                onClick={onSubmitOther}
              >
                Submit
              </button>
            )}
          </>
        )}

        {!otherAnswer && (
          <button
            type="button"
            className={cx('brand-primary-color', s.submitButton)}
            disabled={
              (step === 1 && !selectedCategory) ||
              (step === 2 && !selectedAnswer)
            }
            onClick={step === 1 ? nextStep : submitSelectedAnswer}
          >
            {step === 1 ? 'Next' : 'Submit'}
          </button>
        )}

        {selectedCategory?.type === 'text' && step === 2 ? (
          <button className={s.skipButton} onClick={onSkip}>
            Skip
          </button>
        ) : null}
      </div>
    </>
  );
};
